import { ChevronDown, ChevronUp, X } from "lucide-react";
import {
  ShowBase,
  useGetOne,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";

import { ProjectActions } from "./ProjectActions";
import { ProjectShowContent } from "./ProjectShow";
import { describeTarget, navigationKey, neighbours } from "./panelNavigation";

/** Ouvre une fiche à côté de la liste, en gardant les filtres et la page en cours. */
export const useOpenProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inPanel = location.pathname !== "/projects";
  return useCallback(
    (id?: Identifier) =>
      navigate(
        id == null
          ? `/projects${location.search}`
          : `/projects/${id}/show${location.search}`,
        // Le passage d'une fiche à l'autre ne remplit pas l'historique : « Retour » ramène à la liste.
        { replace: inPanel && id != null },
      ),
    [navigate, location.search, inPanel],
  );
};

/** Fiche en panneau à droite de la liste : J/K ou ↓/↑ pour la voisine, Échap pour fermer. */
export const ProjectPanel = ({ id }: { id: string }) => {
  const translate = useTranslate();
  const open = useOpenProject();
  const { data } = useListContext();
  const { previous, next, position, total } = neighbours(
    (data ?? []).map((record) => record.id),
    id,
  );
  const scroller = useRef<HTMLDivElement>(null);

  // La fiche suivante est demandée d'avance : le passage par J est immédiat.
  useGetOne("projects", { id: next! }, { enabled: next != null });

  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
    document
      .querySelector(".project-row-selected")
      ?.scrollIntoView({ block: "nearest" });
  }, [id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Bulle d'aide ouverte : ses touches (Échap, flèches) ne touchent pas à la fiche.
      if (document.body.classList.contains("driver-active")) return;
      const action = navigationKey({
        key: event.key,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        target: describeTarget(event.target),
      });
      if (!action) return;
      if (action === "close") open();
      else if (action === "next" && next != null) open(next);
      else if (action === "previous" && previous != null) open(previous);
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, previous]);

  return (
    <aside
      aria-label={translate("resources.projects.panel.label")}
      className="sticky top-18 flex h-[calc(100dvh-5.5rem)] w-[560px] shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-background"
    >
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border bg-card px-2">
        <PanelButton
          label={translate("resources.projects.panel.previous")}
          disabled={previous == null}
          onClick={() => open(previous)}
        >
          <ChevronUp />
        </PanelButton>
        <PanelButton
          label={translate("resources.projects.panel.next")}
          disabled={next == null}
          onClick={() => open(next)}
        >
          <ChevronDown />
        </PanelButton>
        {position != null && (
          <span className="ml-1 text-sm tabular-nums text-muted-foreground">
            {translate("resources.projects.panel.position", {
              position,
              total,
            })}
          </span>
        )}
        <span className="flex-1" />
        <span className="mr-2 hidden text-xs text-muted-foreground xl:inline">
          {translate("resources.projects.panel.keys")}
        </span>
        <PanelButton
          label={translate("resources.projects.panel.close")}
          onClick={() => open()}
        >
          <X />
        </PanelButton>
      </div>
      <ShowBase resource="projects" id={id}>
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto p-4">
          <ProjectShowContent />
        </div>
        <ProjectActions />
      </ShowBase>
    </aside>
  );
};

const PanelButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    variant="ghost"
    size="icon"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </Button>
);
